import { useEffect, useMemo, useState } from 'react';
import type {
  CustomerContactRecord,
  UpsertCustomerContactRequest,
} from '@nadoutong/contracts';
import { NotebookPen, Pencil } from 'lucide-react';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateCustomerNote,
  useCustomerContact,
  useCustomerNotes,
  useUpsertCustomerContact,
} from '../api/customerCrm.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Dialog, FormField, Input, Pagination, Panel, Textarea } from '../ui/index.ts';
import { useOpsUserLabel } from '../api/directory.ts';
import { formatLocalTimestamp } from './formatters.ts';
import { useToast } from './Toast.tsx';

/**
 * Self-contained CRM section for the Customer 360 (epic #1035 E6-D5,
 * #1069): the contact card (who to call, on what channel) with an edit
 * dialog, and the append-only follow-up notes timeline with a composer.
 *
 * Server-side scope does the heavy lifting — ops-api only serves these
 * routes to the owner customer manager and the same-brand sales lead
 * (admin passes through), so this component renders whatever the session
 * may see and surfaces errors verbatim. Notes are append-only by design:
 * there is no edit or delete affordance here and no such endpoint in the
 * chain (plain-text v1).
 */

const NOTES_PAGE_LIMIT = 20;

const CONTACT_FIELDS = [
  { key: 'contact_name', labelKey: 'customer_crm.contact.field.contact_name' },
  { key: 'wechat', labelKey: 'customer_crm.contact.field.wechat' },
  { key: 'phone', labelKey: 'customer_crm.contact.field.phone' },
  { key: 'contact_email', labelKey: 'customer_crm.contact.field.contact_email' },
  { key: 'company', labelKey: 'customer_crm.contact.field.company' },
  { key: 'remark', labelKey: 'customer_crm.contact.field.remark' },
] as const satisfies readonly {
  key: keyof UpsertCustomerContactRequest;
  labelKey: I18nKey;
}[];

type ContactDraft = Record<(typeof CONTACT_FIELDS)[number]['key'], string>;

function emptyDraft(): ContactDraft {
  return {
    contact_name: '',
    wechat: '',
    phone: '',
    contact_email: '',
    company: '',
    remark: '',
  };
}

function draftFromContact(contact: CustomerContactRecord | null): ContactDraft {
  if (contact === null) {
    return emptyDraft();
  }
  return {
    contact_name: contact.contact_name ?? '',
    wechat: contact.wechat ?? '',
    phone: contact.phone ?? '',
    contact_email: contact.contact_email ?? '',
    company: contact.company ?? '',
    remark: contact.remark ?? '',
  };
}

function errorText(error: unknown): string {
  if (error instanceof OpsApiError) {
    return `${error.code}: ${error.message}`;
  }
  return error instanceof Error ? error.message : String(error);
}

export function CustomerCrmPanel({
  orgId,
  canWrite,
}: {
  readonly orgId: string;
  /**
   * #1958: contact upserts and note appends are writes ops-api refuses for a
   * finance session, so the card and the note history render without the
   * edit button and the composer.
   */
  readonly canWrite: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const opsUserLabel = useOpsUserLabel();

  const contactQuery = useCustomerContact(orgId);
  const contact = contactQuery.data?.contact ?? null;
  const [editOpen, setEditOpen] = useState(false);

  const [notesPage, setNotesPage] = useState(1);
  const notesScope = useMemo(
    () => ({ limit: NOTES_PAGE_LIMIT, offset: (notesPage - 1) * NOTES_PAGE_LIMIT }),
    [notesPage],
  );
  const notesQuery = useCustomerNotes(orgId, notesScope);
  const notes = notesQuery.data?.notes ?? [];
  const notesTotal = notesQuery.data?.total ?? 0;
  const notesPageCount = Math.max(1, Math.ceil(notesTotal / NOTES_PAGE_LIMIT));

  const [noteDraft, setNoteDraft] = useState('');
  const createNote = useCreateCustomerNote();

  function submitNote(): void {
    const body = noteDraft.trim();
    if (body.length === 0 || createNote.isPending) {
      return;
    }
    createNote.mutate(
      { orgId, body },
      {
        onSuccess: () => {
          setNoteDraft('');
          setNotesPage(1);
          toast.success(t('customer_crm.notes.toast.posted'));
        },
      },
    );
  }

  return (
    <section data-testid="customer-crm-panel" className="grid gap-4 xl:grid-cols-2">
      <Panel
        testId="customer-crm-contact"
        caption={t('customer_crm.contact.caption')}
        actions={
          canWrite ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditOpen(true)}
              disabled={contactQuery.isPending || contactQuery.isError}
              data-testid="customer-crm-contact-edit"
            >
              <span className="inline-flex items-center gap-1.5">
                <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_crm.contact.edit')}
              </span>
            </Button>
          ) : undefined
        }
        padded
      >
        {contactQuery.isPending ? (
          <p className="m-0 text-dense text-paper-low">{t('common.loading')}</p>
        ) : null}
        {contactQuery.isError ? (
          <div
            data-testid="customer-crm-contact-error"
            className="flex flex-col items-start gap-2 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2"
          >
            <p className="m-0 text-dense text-bad-bright">
              {t('customer_crm.error.title')} — {errorText(contactQuery.error)}
            </p>
            <Button variant="ghost" size="sm" onClick={() => void contactQuery.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : null}
        {contactQuery.isSuccess ? (
          contact === null ? (
            <p data-testid="customer-crm-contact-empty" className="m-0 text-dense text-paper-low">
              {t('customer_crm.contact.empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <dl className="m-0 grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {CONTACT_FIELDS.map((field) => (
                  <div key={field.key} className="flex flex-col gap-0.5">
                    <dt className="text-label text-paper-low">{t(field.labelKey)}</dt>
                    <dd
                      className="m-0 text-body text-paper"
                      data-testid={`customer-crm-contact-value-${field.key}`}
                    >
                      {(contact[field.key] ?? '').length > 0 ? contact[field.key] : '—'}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="m-0 text-micro text-paper-low">
                {t('customer_crm.contact.updated', {
                  time: formatLocalTimestamp(contact.updated_at),
                  user: opsUserLabel(contact.updated_by_ops_user_id),
                })}
              </p>
            </div>
          )
        ) : null}
      </Panel>

      <Panel
        testId="customer-crm-notes"
        caption={
          <span className="inline-flex items-center gap-1.5">
            <NotebookPen aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_crm.notes.caption')}
          </span>
        }
        count={notesQuery.isSuccess ? notesTotal : undefined}
        padded
      >
        <div className="flex flex-col gap-3">
          {canWrite ? (
            <div className="flex flex-col gap-2">
              <Textarea
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.currentTarget.value)}
                placeholder={t('customer_crm.notes.composer.placeholder')}
                aria-label={t('customer_crm.notes.composer.aria')}
                rows={3}
                maxLength={4000}
                data-testid="customer-crm-note-composer"
              />
              <div className="flex items-center justify-between gap-2">
                <p className="m-0 text-micro text-paper-low">
                  {t('customer_crm.notes.append_only_hint')}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={submitNote}
                  loading={createNote.isPending}
                  disabled={noteDraft.trim().length === 0}
                  data-testid="customer-crm-note-submit"
                >
                  {t('customer_crm.notes.composer.submit')}
                </Button>
              </div>
              {createNote.isError ? (
                <p
                  role="alert"
                  data-testid="customer-crm-note-error"
                  className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
                >
                  {errorText(createNote.error)}
                </p>
              ) : null}
            </div>
          ) : null}

          {notesQuery.isPending ? (
            <p className="m-0 text-dense text-paper-low">{t('common.loading')}</p>
          ) : null}
          {notesQuery.isError ? (
            <div
              data-testid="customer-crm-notes-error"
              className="flex flex-col items-start gap-2 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2"
            >
              <p className="m-0 text-dense text-bad-bright">
                {t('customer_crm.error.title')} — {errorText(notesQuery.error)}
              </p>
              <Button variant="ghost" size="sm" onClick={() => void notesQuery.refetch()}>
                {t('common.retry')}
              </Button>
            </div>
          ) : null}
          {notesQuery.isSuccess ? (
            notes.length === 0 ? (
              <p data-testid="customer-crm-notes-empty" className="m-0 text-dense text-paper-low">
                {t('customer_crm.notes.empty')}
              </p>
            ) : (
              <ol className="m-0 flex list-none flex-col gap-2 p-0">
                {notes.map((note) => (
                  <li
                    key={note.id}
                    data-testid="customer-crm-note"
                    className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-label font-medium text-paper">
                        {note.author_display_name ?? note.author_ops_user_id}
                      </span>
                      <span className="font-mono text-micro text-paper-low">
                        {formatLocalTimestamp(note.created_at)}
                      </span>
                    </div>
                    <p className="m-0 whitespace-pre-wrap text-dense text-paper-mid">{note.body}</p>
                  </li>
                ))}
              </ol>
            )
          ) : null}

          {notesPageCount > 1 ? (
            <Pagination
              page={notesPage}
              pageCount={notesPageCount}
              onPageChange={setNotesPage}
              prevLabel={t('common.previous')}
              nextLabel={t('common.next')}
              ariaLabel={t('customer_crm.notes.pagination_aria')}
              testId="customer-crm-notes-pagination"
            />
          ) : null}
        </div>
      </Panel>

      {canWrite ? (
        <ContactEditDialog
          orgId={orgId}
          open={editOpen}
          onOpenChange={setEditOpen}
          contact={contact}
        />
      ) : null}
    </section>
  );
}

function ContactEditDialog({
  orgId,
  open,
  onOpenChange,
  contact,
}: {
  readonly orgId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly contact: CustomerContactRecord | null;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft());
  const upsert = useUpsertCustomerContact();

  useEffect(() => {
    if (open) {
      setDraft(draftFromContact(contact));
      upsert.reset();
    }
    // Intentionally seeded only on open: while the dialog is up, the
    // draft belongs to the operator, not to background refetches.
  }, [open]);

  function submit(): void {
    const body: UpsertCustomerContactRequest = {};
    for (const field of CONTACT_FIELDS) {
      const value = draft[field.key].trim();
      body[field.key] = value.length > 0 ? value : null;
    }
    upsert.mutate(
      { orgId, body },
      {
        onSuccess: () => {
          toast.success(t('customer_crm.contact.toast.saved'));
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!upsert.isPending) {
          onOpenChange(next);
        }
      }}
      title={t('customer_crm.contact.dialog.title')}
      description={t('customer_crm.contact.dialog.description')}
      closeLabel={t('common.close')}
      testId="customer-crm-contact-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
            data-testid="customer-crm-contact-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={upsert.isPending}
            data-testid="customer-crm-contact-save"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {CONTACT_FIELDS.map((field) => (
          <FormField
            key={field.key}
            label={t(field.labelKey)}
            fieldId={`customer-crm-contact-input-${field.key}`}
            className={field.key === 'remark' ? 'sm:col-span-2' : undefined}
          >
            <Input
              id={`customer-crm-contact-input-${field.key}`}
              value={draft[field.key]}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setDraft((current) => ({ ...current, [field.key]: value }));
              }}
              maxLength={field.key === 'remark' ? 2000 : 200}
              data-testid={`customer-crm-contact-input-${field.key}`}
            />
          </FormField>
        ))}
      </div>
      {upsert.isError ? (
        <p
          role="alert"
          data-testid="customer-crm-contact-error-dialog"
          className="mt-3 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
        >
          {errorText(upsert.error)}
        </p>
      ) : null}
    </Dialog>
  );
}
